import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getShiftGroups } from '../../graphql/shift-turnovers/get-shift-groups';
import { BaseAppSyncService } from '../base-app-sync.service';
import { queryForShiftTurnovers } from '../../graphql/shift-turnovers/query-for-shift-turnover';
import { getShiftTurnover } from '../../graphql/shift-turnovers/get-shift-turnover';
import { getShiftTypes } from '../../graphql/shift-turnovers/get-shift-types';
import { createShiftTurnover } from '../../graphql/shift-turnovers/create-shift-turnover';
import { updateShiftTurnover } from '../../graphql/shift-turnovers/update-shift-turnover';
import { getShiftTurnoverHistories } from '../../graphql/shift-turnovers/get-shift-turnover-history';
import { deleteShiftTurnover } from '../../graphql/shift-turnovers/delete-shift-turnover';
import { buildAuditHistory } from 'src/app/core/functions/common-functions';
import { Audit } from 'src/app/model/common/audit';
import { ShiftTurnoverBuilderService } from '../builder/shift-turnover-builder.service';
import {
  ShiftTurnover,
  UpdateShiftTurnoverInput,
} from 'src/app/model/shift/shift-turnover';
import { ShiftGroup } from 'src/app/model/shift/shift-group';
import { ShiftType } from 'src/app/model/shift/shift-type';
import {
  ShiftTurnoverConnection,
  ShiftTurnoverSortOrQuery,
} from 'src/app/model/shift/shift-turnover-search-results';

@Injectable({
  providedIn: 'root',
})
export class ShiftTurnoverApiService extends BaseAppSyncService {
  _shiftTurnoverBuilder = inject(ShiftTurnoverBuilderService);

  queryForShiftTurnovers(
    limit: number,
    page: number,
    query: ShiftTurnoverSortOrQuery,
    sort: ShiftTurnoverSortOrQuery
  ): Observable<ShiftTurnoverConnection> {
    return this.query(
      queryForShiftTurnovers,
      { limit, page, query, sort },
      (i: any) => this._shiftTurnoverBuilder.buildShiftTurnoverConnection(i),
      'queryForShiftTurnovers'
    );
  }

  getShiftTurnover(id: string): Observable<ShiftTurnover> {
    return this.query(
      getShiftTurnover,
      { id },
      (i: any) => this._shiftTurnoverBuilder.buildShiftTurnover(i),
      'getShiftTurnover'
    );
  }

  getShiftGroups(): Observable<ShiftGroup[]> {
    return this.query(
      getShiftGroups,
      {},
      (items: any[]) => items,
      'getShiftGroups',
      'cache-first'
    );
  }

  getShiftTypes(): Observable<ShiftType[]> {
    return this.query(
      getShiftTypes,
      {},
      (items: any[]) => items,
      'getShiftTypes',
      'cache-first'
    );
  }

  createShiftTurnover(input: any): Observable<ShiftTurnover> {
    return this.mutate(
      createShiftTurnover,
      { input },
      (i: any) => this._shiftTurnoverBuilder.buildShiftTurnover(i),
      'createShiftTurnover'
    );
  }

  updateShiftTurnover(
    id: string,
    input: UpdateShiftTurnoverInput
  ): Observable<ShiftTurnover> {
    return this.mutate(
      updateShiftTurnover,
      { id, input },
      (turnover: any) =>
        this._shiftTurnoverBuilder.buildShiftTurnover(turnover),
      'updateShiftTurnover'
    );
  }

  getShiftTurnoverHistories(id: string): Observable<Audit[]> {
    return this.query(
      getShiftTurnoverHistories,
      { id },
      (histories: any[]) => buildAuditHistory(histories),
      'getShiftTurnoverHistories'
    );
  }

  deleteShiftTurnover(id: string): Observable<boolean> {
    return this.mutate(
      deleteShiftTurnover,
      { id },
      (i: any) => i.value,
      'deleteShiftTurnover'
    );
  }
}
